// Settings.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { showHUD } from './NotificationHUD';
import {
  User as UserIcon,
  Mail,
  Lock,
  UserX,
  HardDrive,
  RefreshCw,
  Camera,
  Check,
  AlertCircle,
  Sparkles,
  LogOut,
  Database,
} from 'lucide-react';
import {
  getAuthUser,
  saveAuthUser,
  UserProfile,
} from '../repositories/LocalDatabase';
import { supabaseManager, SupabaseProject } from '../utils/supabaseManager';
import ConfirmModal from './ConfirmModal';

interface CustomModalState {
  isOpen: boolean;
  type: 'confirm' | 'alert';
  title: string;
  message: string;
  onConfirm?: () => void;
}

export default function Settings() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Supabase integration state
  const [supabaseProjects, setSupabaseProjects] = useState<SupabaseProject[]>([]);
  const [selectedProjectRef, setSelectedProjectRef] = useState<string>('');
  const [isSupabaseInitialized, setIsSupabaseInitialized] = useState(supabaseManager.isInitialized);
  const [isSupabaseLoading, setIsSupabaseLoading] = useState(false);

  const [profile, setProfile] = useState<UserProfile>({
    username: 'no name',
    email: 'user@example.com',
    avatarUrl: '',
    isLoggedIn: false,
  });

  const [usernameInput, setUsernameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordConfirmInput, setPasswordConfirmInput] = useState('');

  const [animationsEnabled, setAnimationsEnabled] = useState(() => {
    return localStorage.getItem('animationsEnabled') !== 'false';
  });

  const handleToggleAnimations = (checked: boolean) => {
    localStorage.setItem('animationsEnabled', String(checked));
    setAnimationsEnabled(checked);
    document.body.classList.toggle('no-animations', !checked);
    window.dispatchEvent(new CustomEvent('hf-animation-settings-change', { detail: checked }));
    showHUD({
      type: 'success',
      message: checked ? 'アニメーションを有効にしました' : 'アニメーションを無効にしました',
    });
  };

  const [errorProfile, setErrorProfile] = useState('');
  const [successProfile, setSuccessProfile] = useState('');
  const [errorPassword, setErrorPassword] = useState('');
  const [successPassword, setSuccessPassword] = useState('');

  // Custom Modal State
  const [modalConfig, setModalConfig] = useState<CustomModalState>({
    isOpen: false,
    type: 'alert',
    title: '',
    message: '',
  });

  const showCustomAlert = (
    title: string,
    message: string,
    onConfirm?: () => void,
  ) => {
    setModalConfig({
      isOpen: true,
      type: 'alert',
      title,
      message,
      onConfirm: () => {
        setModalConfig((prev) => ({ ...prev, isOpen: false }));
        if (onConfirm) onConfirm();
      },
    });
  };

  const showCustomConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
  ) => {
    setModalConfig({
      isOpen: true,
      type: 'confirm',
      title,
      message,
      onConfirm: () => {
        setModalConfig((prev) => ({ ...prev, isOpen: false }));
        onConfirm();
      },
    });
  };

  useEffect(() => {
    if (supabaseManager.isConfigured) {
      setSupabaseProjects(supabaseManager.getProjects());
      setSelectedProjectRef(supabaseManager.getCurrentProjectRef() || '');
      setIsSupabaseInitialized(supabaseManager.isInitialized);
    }
  }, []);

  const handleProjectChange = async (ref: string) => {
    setIsSupabaseLoading(true);
    setSelectedProjectRef(ref);
    try {
      const success = await supabaseManager.initialize(ref);
      if (success) {
        setIsSupabaseInitialized(true);
        showHUD({ type: 'success', message: 'Supabase プロジェクトを切り替えました' });
      } else {
        setIsSupabaseInitialized(false);
        showHUD({ type: 'error', message: 'プロジェクトの切り替えに失敗しました' });
      }
    } catch (err) {
      setIsSupabaseInitialized(false);
      showHUD({ type: 'error', message: 'プロジェクトの切り替え中にエラーが発生しました' });
    } finally {
      setIsSupabaseLoading(false);
    }
  };

  useEffect(() => {
    const user = getAuthUser();
    setProfile(user);
    setUsernameInput(user.username);
    setEmailInput(user.email);

    const handleAuthChange = () => {
      const u = getAuthUser();
      setProfile(u);
      setUsernameInput(u.username);
      setEmailInput(u.email);
    };

    window.addEventListener('hf-auth-change', handleAuthChange);
    return () => {
      window.removeEventListener('hf-auth-change', handleAuthChange);
    };
  }, []);

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      setErrorProfile('画像形式はPNGまたはJPEGのみアップロード可能です。');
      showHUD({ type: 'error', message: 'PNG/JPEGのみ可能です' });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result as string;
      const updated = { ...profile, avatarUrl: base64String };
      saveAuthUser(updated);

      if (supabaseManager.isInitialized) {
        try {
          await supabaseManager.updateProfile(usernameInput || profile.username || 'no name', base64String);
        } catch (err) {
          console.error('Failed to save avatar to remote DB:', err);
        }
      }

      setSuccessProfile('プロフィールアバターを更新しました。');
      showHUD({ type: 'success', message: 'アバターを更新しました' });
      setTimeout(() => setSuccessProfile(''), 3000);
    };
    reader.readAsDataURL(file);
  };

  const handleResetAvatar = async () => {
    setErrorProfile('');
    setSuccessProfile('');
    const updated = { ...profile, avatarUrl: '' };
    saveAuthUser(updated);

    if (supabaseManager.isInitialized) {
      try {
        await supabaseManager.updateProfile(usernameInput || profile.username || 'no name', '');
      } catch (err) {
        console.error('Failed to reset avatar in remote DB:', err);
      }
    }

    setSuccessProfile('プロフィールアバターを初期画像に戻しました。');
    showHUD({ type: 'success', message: 'アバターを初期化しました' });
    setTimeout(() => setSuccessProfile(''), 3000);
  };

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorProfile('');
    setSuccessProfile('');

    if (!emailInput) {
      setErrorProfile('メールアドレスは必須項目です。');
      showHUD({ type: 'error', message: 'メールアドレスは必須です' });
      return;
    }

    const isEmailChanged = emailInput !== profile.email;

    if (supabaseManager.isInitialized) {
      try {
        const { error } = await supabaseManager.updateProfile(usernameInput || 'no name', profile.avatarUrl);
        if (error) {
          setErrorProfile(`プロフィールの更新に失敗しました: ${error.message}`);
          showHUD({ type: 'error', message: '更新に失敗しました' });
          return;
        }
      } catch (err: any) {
        setErrorProfile(`通信エラーが発生しました: ${err.message}`);
        showHUD({ type: 'error', message: '通信エラーが発生しました' });
        return;
      }
    }

    const updated = {
      ...profile,
      username: usernameInput || 'no name',
      email: emailInput,
    };

    saveAuthUser(updated);

    if (isEmailChanged) {
      showCustomAlert(
        '登録メールアドレス変更',
        '登録メールアドレスが変更されました。確認用のメールを送信しましたのでご確認ください。',
      );
    }

    setSuccessProfile('プロフィール情報を保存しました。');
    showHUD({ type: 'success', message: '変更を保存しました' });
    setTimeout(() => setSuccessProfile(''), 3000);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorPassword('');
    setSuccessPassword('');

    if (passwordInput.length < 6) {
      setErrorPassword('パスワードは6文字以上で入力してください。');
      showHUD({ type: 'error', message: '6文字以上必要です' });
      return;
    }

    const hasUpper = /[A-Z]/.test(passwordInput);
    const hasLower = /[a-z]/.test(passwordInput);
    if (!hasUpper || !hasLower) {
      setErrorPassword(
        'パスワードには大文字と小文字をそれぞれ1文字以上含めてください。',
      );
      showHUD({ type: 'error', message: '大文字/小文字が必要です' });
      return;
    }

    if (passwordInput !== passwordConfirmInput) {
      setErrorPassword('新しいパスワードと確認用パスワードが一致しません。');
      showHUD({ type: 'error', message: 'パスワードが一致しません' });
      return;
    }

    if (supabaseManager.isInitialized) {
      try {
        const { error } = await supabaseManager.updatePassword(passwordInput);
        if (error) {
          setErrorPassword(`パスワードの変更に失敗しました: ${error.message}`);
          showHUD({ type: 'error', message: '変更に失敗しました' });
          return;
        }
        setSuccessPassword('パスワードを更新しました。');
        showHUD({ type: 'success', message: 'パスワードを変更しました' });
        setPasswordInput('');
        setPasswordConfirmInput('');
        setTimeout(() => setSuccessPassword(''), 5000);
      } catch (err: any) {
        setErrorPassword(`通信エラーが発生しました: ${err.message}`);
        showHUD({ type: 'error', message: '通信エラーが発生しました' });
      }
    } else {
      showCustomAlert(
        'パスワード変更手続き',
        'パスワード変更の確認用メールを送信しました。メールに記載されたリンクから変更を完了してください。',
        () => {
          setSuccessPassword('パスワード変更手続きメールを送信しました。');
          showHUD({ type: 'success', message: 'メールを送信しました' });
          setPasswordInput('');
          setPasswordConfirmInput('');
          setTimeout(() => setSuccessPassword(''), 5000);
        },
      );
    }
  };

  const handleLogout = () => {
    showCustomConfirm(
      'ログアウト',
      '現在のアカウントからログアウトしますか？\nユーザー情報は保持されます。',
      async () => {
        if (supabaseManager.isInitialized) {
          await supabaseManager.signOut();
        }
        const updated = { ...profile, isLoggedIn: false };
        saveAuthUser(updated);
        showHUD({ type: 'success', message: 'ログアウトしました' });
        navigate('/');
      }
    );
  };

  const handleWithdrawal = () => {
    showCustomConfirm(
      'アカウント退会',
      '本当に退会しますか？\n登録したレシピやフレーバーカードは保持されますが、投稿者名は「退会済みユーザー」となります。この操作は取り消せません。',
      () => {
        // 1. mixes テーブルの user_id === 'local-user' を 'withdrawn-user' に書き換える
        try {
          const storedMixes = localStorage.getItem('hf_mixes');
          if (storedMixes) {
            const mixesList = JSON.parse(storedMixes) as any[];
            const updatedMixes = mixesList.map((mix) => {
              if (mix.user_id === 'local-user') {
                return { ...mix, user_id: 'withdrawn-user' };
              }
              return mix;
            });
            localStorage.setItem('hf_mixes', JSON.stringify(updatedMixes));
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to update mixes during withdrawal', err);
        }

        // 2. 認証データを初期化
        const defaultUser: UserProfile = {
          username: 'no name',
          email: 'user@example.com',
          avatarUrl: '',
          isLoggedIn: false,
        };
        saveAuthUser(defaultUser);
        showCustomAlert(
          '退会完了',
          '退会処理が完了しました。ご利用ありがとうございました。',
          () => {
            navigate('/');
          },
        );
      },
    );
  };

  const handleResetDb = () => {
    showCustomConfirm(
      'データベースリセット',
      'ローカルの全データを消去し、初期状態にリセットしますか？この操作は取り消せません。',
      () => {
        localStorage.clear();
        showCustomAlert(
          'リセット完了',
          'データをリセットしました。アプリケーションを再読み込みします。',
          () => {
            window.location.reload();
          },
        );
      },
    );
  };

  return (
    <div
      style={{
        padding: '30px 20px',
        maxWidth: '800px',
        margin: '0 auto',
        animation: 'fadeIn 0.5s ease-out',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
      }}
    >
      {/* Header */}
      <div
        style={{
          borderBottom: '1px solid rgba(231, 228, 223, 0.1)',
          paddingBottom: '16px',
        }}
      >
        <h1
          style={{
            fontSize: 'var(--font-size-h1)',
            fontFamily: 'var(--font-heading-ja)',
            color: 'var(--color-text)',
            margin: 0,
          }}
        >
          設定
        </h1>
        <p
          style={{
            margin: '6px 0 0 0',
            fontSize: 'var(--font-size-caption)',
            opacity: 0.7,
          }}
        >
          アカウントプロフィールの管理、セキュリティおよびデータベースメンテナンス
        </p>
      </div>

      {/* Profile Section */}
      <div
        className="glass-panel"
        style={{
          padding: '28px',
          backgroundColor: 'var(--color-surface)',
          borderRadius: '16px',
          border: '1px solid rgba(231, 228, 223, 0.08)',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--font-size-h3)',
            fontFamily: 'var(--font-heading-ja)',
            margin: 0,
            color: 'var(--color-accent)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <UserIcon size={20} />
          プロフィール設定
        </h3>

        {/* Avatar Upload */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
          }}
        >
          <div
            onClick={handleAvatarClick}
            style={{
              position: 'relative',
              width: '80px',
              height: '80px',
              flexShrink: 0,
              borderRadius: '50%',
              backgroundColor: 'rgba(26, 28, 31, 0.6)',
              border: '1px solid rgba(231, 228, 223, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              overflow: 'hidden',
            }}
          >
            {profile.avatarUrl ? (
              <img
                src={profile.avatarUrl}
                alt="Avatar"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <UserIcon size={32} style={{ opacity: 0.5 }} />
            )}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '24px',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Camera size={12} color="#fff" />
            </div>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/png, image/jpeg"
            style={{ display: 'none' }}
          />
          <div>
            <span
              style={{
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'block',
                marginBottom: '4px',
              }}
            >
              プロフィールアバター
            </span>
            <span
              style={{
                fontSize: '12px',
                opacity: 0.6,
                display: 'block',
              }}
            >
              PNGまたはJPEG形式の画像。全ページの右上に表示されます。
            </span>
            {profile.avatarUrl && (
              <button
                type="button"
                onClick={handleResetAvatar}
                style={{
                  marginTop: '8px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(231, 228, 223, 0.1)',
                  color: 'var(--color-text)',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    'rgba(212, 106, 106, 0.1)';
                  e.currentTarget.style.borderColor =
                    'rgba(212, 106, 106, 0.3)';
                  e.currentTarget.style.color = 'var(--color-error)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor =
                    'rgba(231, 228, 223, 0.1)';
                  e.currentTarget.style.color = 'var(--color-text)';
                }}
              >
                初期画像に戻す
              </button>
            )}
          </div>
        </div>

        {/* Profile Form */}
        <form
          onSubmit={handleProfileSave}
          style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
        >
          {errorProfile && (
            <div
              style={{
                color: 'var(--color-error)',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <AlertCircle size={14} />
              {errorProfile}
            </div>
          )}
          {successProfile && (
            <div
              style={{
                color: 'var(--color-success)',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Check size={14} />
              {successProfile}
            </div>
          )}

          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            <label style={{ fontSize: '14px', opacity: 0.8 }}>
              ユーザー名
            </label>
            <input
              type="text"
              value={usernameInput}
              onChange={(e) => setUsernameInput(e.target.value)}
              placeholder="no name"
              style={{
                width: '100%',
                backgroundColor: 'rgba(26, 28, 31, 0.6)',
                border: '1px solid rgba(231, 228, 223, 0.1)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                padding: '12px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>

          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            <label style={{ fontSize: '14px', opacity: 0.8 }}>
              登録メールアドレス（必須）
            </label>
            <input
              type="email"
              required
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="email@example.com"
              style={{
                width: '100%',
                backgroundColor: 'rgba(26, 28, 31, 0.6)',
                border: '1px solid rgba(231, 228, 223, 0.1)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                padding: '12px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>

          <button
            type="submit"
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              backgroundColor: 'var(--color-primary)',
              border: 'none',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              alignSelf: 'flex-start',
              boxShadow: '0 4px 12px rgba(107, 78, 255, 0.2)',
            }}
          >
            変更を保存
          </button>
        </form>
      </div>

      {/* Security Section (Password) */}
      <div
        className="glass-panel"
        style={{
          padding: '28px',
          backgroundColor: 'var(--color-surface)',
          borderRadius: '16px',
          border: '1px solid rgba(231, 228, 223, 0.08)',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--font-size-h3)',
            fontFamily: 'var(--font-heading-ja)',
            margin: 0,
            color: 'var(--color-accent)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Lock size={20} />
          セキュリティ
        </h3>

        <form
          onSubmit={handlePasswordChange}
          style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
        >
          {errorPassword && (
            <div
              style={{
                color: 'var(--color-error)',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <AlertCircle size={14} />
              {errorPassword}
            </div>
          )}
          {successPassword && (
            <div
              style={{
                color: 'var(--color-success)',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Check size={14} />
              {successPassword}
            </div>
          )}

          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            <label style={{ fontSize: '14px', opacity: 0.8 }}>
              新しいパスワード
              <span
                style={{ fontSize: '11px', opacity: 0.6, display: 'block' }}
              >
                （英大文字・小文字各1文字以上含む、6文字以上）
              </span>
            </label>
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="••••••••"
              style={{
                width: '100%',
                backgroundColor: 'rgba(26, 28, 31, 0.6)',
                border: '1px solid rgba(231, 228, 223, 0.1)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                padding: '12px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>

          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            <label style={{ fontSize: '14px', opacity: 0.8 }}>
              新しいパスワード（確認用）
            </label>
            <input
              type="password"
              value={passwordConfirmInput}
              onChange={(e) => setPasswordConfirmInput(e.target.value)}
              placeholder="••••••••"
              style={{
                width: '100%',
                backgroundColor: 'rgba(26, 28, 31, 0.6)',
                border: '1px solid rgba(231, 228, 223, 0.1)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                padding: '12px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>

          <button
            type="submit"
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              backgroundColor: 'var(--color-primary)',
              border: 'none',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              alignSelf: 'flex-start',
              boxShadow: '0 4px 12px rgba(107, 78, 255, 0.2)',
            }}
          >
            パスワードを変更
          </button>
        </form>
      </div>

      {/* Supabase Integration Section */}
      {supabaseManager.isConfigured && (
        <div
          className="glass-panel"
          style={{
            padding: '28px',
            backgroundColor: 'var(--color-surface)',
            borderRadius: '16px',
            border: '1px solid rgba(231, 228, 223, 0.08)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          <h3
            style={{
              fontSize: 'var(--font-size-h3)',
              fontFamily: 'var(--font-heading-ja)',
              margin: 0,
              color: 'var(--color-accent)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <Database size={20} />
            Supabase 連携設定
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>接続ステータス</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
              <span
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: isSupabaseInitialized ? 'var(--color-success)' : 'var(--color-error)',
                  display: 'inline-block',
                }}
              />
              {isSupabaseInitialized ? (
                <span style={{ color: 'var(--color-success)' }}>連携済み (オンライン)</span>
              ) : (
                <span style={{ color: 'var(--color-error)' }}>未連携 / エラー</span>
              )}
            </div>
            <span style={{ fontSize: '12px', opacity: 0.6 }}>
              .env.local 内のパーソナルアクセストークンを使用して自動接続されています。
            </span>
          </div>

          {supabaseProjects.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', opacity: 0.8 }}>
                使用する Supabase プロジェクト
              </label>
              <select
                value={selectedProjectRef}
                disabled={isSupabaseLoading}
                onChange={(e) => handleProjectChange(e.target.value)}
                style={{
                  width: '100%',
                  backgroundColor: 'rgba(26, 28, 31, 0.6)',
                  border: '1px solid rgba(231, 228, 223, 0.1)',
                  borderRadius: '8px',
                  color: 'var(--color-text)',
                  padding: '12px',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: isSupabaseLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {supabaseProjects.map((p) => (
                  <option key={p.id} value={p.id} style={{ backgroundColor: 'var(--color-surface)' }}>
                    {p.name} ({p.region})
                  </option>
                ))}
              </select>
              <span style={{ fontSize: '12px', opacity: 0.6 }}>
                選択したプロジェクトの API URL と Anon Key が自動的に取得され、認証機能で使用されます。
              </span>
            </div>
          )}
        </div>
      )}

      {/* Animation Settings Section */}
      <div
        className="glass-panel"
        style={{
          padding: '28px',
          backgroundColor: 'var(--color-surface)',
          borderRadius: '16px',
          border: '1px solid rgba(231, 228, 223, 0.08)',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--font-size-h3)',
            fontFamily: 'var(--font-heading-ja)',
            margin: 0,
            color: 'var(--color-accent)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Sparkles size={20} />
          アニメーション設定
        </h3>
        <p
          style={{
            margin: 0,
            fontSize: '13px',
            opacity: 0.7,
            lineHeight: '1.5',
            color: 'var(--color-text)',
          }}
        >
          アプリ内のアニメーション効果を管理します。無効化すると、モーダルのフェード表示やボタンの波紋エフェクト、およびトップページの煙の演出（静止画として描画されます）が停止し、動作が軽量化されます。
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input
            type="checkbox"
            id="animations-toggle"
            checked={animationsEnabled}
            onChange={(e) => handleToggleAnimations(e.target.checked)}
            style={{
              width: '18px',
              height: '18px',
              cursor: 'pointer',
              accentColor: 'var(--color-primary)',
            }}
          />
          <label
            htmlFor="animations-toggle"
            style={{
              fontSize: '14px',
              cursor: 'pointer',
              userSelect: 'none',
              color: 'var(--color-text)',
            }}
          >
            アニメーションを有効にする
          </label>
        </div>
      </div>

      {/* Database Maintenance Section */}
      <div
        className="glass-panel"
        style={{
          padding: '28px',
          backgroundColor: 'var(--color-surface)',
          borderRadius: '16px',
          border: '1px solid rgba(231, 228, 223, 0.08)',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--font-size-h3)',
            fontFamily: 'var(--font-heading-ja)',
            margin: 0,
            color: 'var(--color-error)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <HardDrive size={20} />
          データベース管理
        </h3>
        <p
          style={{
            margin: 0,
            fontSize: '13px',
            opacity: 0.7,
            lineHeight: '1.5',
          }}
        >
          ローカルストレージのデータを初期化し、モックマスターデータを再投入します。登録されたマイミックス、カスタマイズした味覚ベクトル、所持フラグはすべて消去されます。
        </p>

        <div>
          <button
            onClick={handleResetDb}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              borderRadius: '8px',
              backgroundColor: 'rgba(212, 106, 106, 0.1)',
              border: '1px solid rgba(212, 106, 106, 0.3)',
              color: 'var(--color-error)',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={16} />
            ローカルデータベースをリセット
          </button>
        </div>
      </div>

      {/* Logout Section */}
      {profile.isLoggedIn && (
        <div
          className="glass-panel"
          style={{
            padding: '28px',
            backgroundColor: 'var(--color-surface)',
            borderRadius: '16px',
            border: '1px solid rgba(231, 228, 223, 0.08)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginBottom: '30px',
          }}
        >
          <h3
            style={{
              fontSize: 'var(--font-size-h3)',
              fontFamily: 'var(--font-heading-ja)',
              margin: 0,
              color: 'var(--color-text)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <LogOut size={20} />
            ログアウト
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: '13px',
              opacity: 0.7,
              lineHeight: '1.5',
              color: 'var(--color-text)',
            }}
          >
            ユーザー情報を保持したままログアウトします。再度ログインすることで引き続きご利用いただけます。
          </p>

          <div>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                borderRadius: '8px',
                backgroundColor: 'rgba(231, 228, 223, 0.05)',
                border: '1px solid rgba(231, 228, 223, 0.2)',
                color: 'var(--color-text)',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(231, 228, 223, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(231, 228, 223, 0.05)';
              }}
            >
              <LogOut size={16} />
              ログアウトする
            </button>
          </div>
        </div>
      )}

      {/* Withdrawal Section */}
      {profile.isLoggedIn && (
        <div
          className="glass-panel"
          style={{
            padding: '28px',
            backgroundColor: 'var(--color-surface)',
            borderRadius: '16px',
            border: '1px solid rgba(231, 228, 223, 0.08)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <h3
            style={{
              fontSize: 'var(--font-size-h3)',
              fontFamily: 'var(--font-heading-ja)',
              margin: 0,
              color: 'var(--color-error)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <UserX size={20} />
            アカウントの退会
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: '13px',
              opacity: 0.7,
              lineHeight: '1.5',
              color: 'var(--color-text)',
            }}
          >
            ユーザー情報を削除し、退会します。登録されているフレーバーカードやミックスカードは保持されますが、ミックスカードの投稿者名には「退会済みユーザー」と表示されます。この操作は取り消せません。
          </p>

          <div>
            <button
              onClick={handleWithdrawal}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                borderRadius: '8px',
                backgroundColor: 'rgba(212, 106, 106, 0.1)',
                border: '1px solid rgba(212, 106, 106, 0.3)',
                color: 'var(--color-error)',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              <UserX size={16} />
              退会する
            </button>
          </div>
        </div>
      )}

      {/* Use ConfirmModal instead of custom inline modal */}
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        isAlert={modalConfig.type === 'alert'}
        isDestructive={
          modalConfig.title.includes('退会') || modalConfig.title.includes('リセット')
        }
        confirmText={modalConfig.type === 'confirm' ? '実行する' : 'OK'}
        onConfirm={modalConfig.onConfirm || (() => {})}
        onCancel={() => setModalConfig((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
